import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft, Check, AlertCircle, Loader2, Eye, EyeOff, Lock, ShieldCheck } from "lucide-react";
import { resetPassword } from "@/services/authService";
import { Progress } from "@/components/ui/progress";

// Form schema with password validation
const resetPasswordSchema = z.object({
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters" })
    .regex(/[A-Z]/, { message: "Password must contain at least one uppercase letter" })
    .regex(/[a-z]/, { message: "Password must contain at least one lowercase letter" })
    .regex(/[0-9]/, { message: "Password must contain at least one number" }),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Extract token from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenParam = searchParams.get('token');
    
    if (!tokenParam) {
      setTokenError(true);
      toast({
        title: "Invalid Reset Link",
        description: "The password reset link is invalid or has expired.",
        variant: "destructive",
      });
    } else {
      setToken(tokenParam);
    }
  }, [location, toast]);

  // Initialize form
  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  // Calculate password strength
  const calculatePasswordStrength = (password: string) => {
    let score = 0;
    
    if (password.length >= 8) score += 25;
    if (/[A-Z]/.test(password)) score += 25;
    if (/[a-z]/.test(password)) score += 25;
    if (/[0-9]/.test(password)) score += 25;
    
    setPasswordStrength(score);
  };

  // Watch password field for strength calculation
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'password' && value.password) {
        calculatePasswordStrength(value.password as string);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Get strength color
  const getStrengthColor = () => {
    if (passwordStrength <= 25) return "bg-red-500";
    if (passwordStrength <= 50) return "bg-orange-500";
    if (passwordStrength <= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  // Get strength text
  const getStrengthText = () => {
    if (passwordStrength <= 25) return "Weak";
    if (passwordStrength <= 50) return "Fair";
    if (passwordStrength <= 75) return "Good";
    return "Strong";
  };

  // Handle form submission
  const onSubmit = async (data: ResetPasswordFormValues) => {
    if (!token) return;
    
    setIsSubmitting(true);
    try {
      // Call the resetPassword service with token and new password
      const response = await resetPassword(token, data.password);

      if (response.success) {
        setResetComplete(true);
        toast({
          title: "Password Reset Successful",
          description: response.message || "Your password has been reset successfully. You can now log in with your new password.",
          variant: "default",
        });
      } else {
        throw new Error(response.error || "Password reset failed");
      }
    } catch (error: any) {
      console.error("Password reset failed:", error);
      toast({
        title: "Password Reset Failed",
        description: error.message || "There was a problem resetting your password. Please try again or request a new reset link.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect to login after successful reset
  const handleLoginRedirect = () => {
    navigate("/signin");
  };

  if (tokenError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md shadow-lg animate-fadeIn">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">Invalid Reset Link</CardTitle>
            <CardDescription className="text-center">
              The password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="mx-auto bg-red-100 rounded-full p-3 w-16 h-16 flex items-center justify-center mb-6">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Please request a new password reset link to continue.
            </p>
            <Button 
              onClick={() => navigate("/forgot-password")}
              className="w-full"
              size="lg"
            >
              Request New Link
            </Button>
          </CardContent>
          <CardFooter className="flex justify-center pt-2">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/signin")}
              className="text-sm"
            >
              Return to Sign In
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md shadow-lg animate-fadeIn">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => navigate("/signin")}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to login</span>
            </Button>
            <div className="flex-1 text-center">
              <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
            </div>
            <div className="w-8"></div> {/* Spacer for alignment */}
          </div>
          <CardDescription className="text-center">
            Create a new password for your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {resetComplete ? (
            <div className="text-center space-y-6 py-4">
              <div className="mx-auto bg-green-100 rounded-full p-4 w-20 h-20 flex items-center justify-center">
                <ShieldCheck className="h-10 w-10 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-medium mb-2">Password Reset Complete</h3>
                <p className="text-sm text-muted-foreground">
                  Your password has been reset successfully. Your account is now secure with your new password.
                </p>
              </div>
              <Button 
                onClick={handleLoginRedirect} 
                className="w-full" 
                size="lg"
              >
                Sign In Now
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-primary/10 rounded-full p-3">
                      <Lock className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                </div>
                
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input 
                            placeholder="Enter new password" 
                            type={showPassword ? "text" : "password"} 
                            {...field} 
                            autoComplete="new-password"
                            className="pr-10"
                            onChange={(e) => {
                              field.onChange(e);
                              calculatePasswordStrength(e.target.value);
                            }}
                          />
                        </FormControl>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="sr-only">
                            {showPassword ? "Hide password" : "Show password"}
                          </span>
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {form.watch("password") && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Password strength:</span>
                      <span className={`text-xs font-medium ${
                        passwordStrength <= 25 ? "text-red-500" : 
                        passwordStrength <= 50 ? "text-orange-500" : 
                        passwordStrength <= 75 ? "text-yellow-500" : 
                        "text-green-500"
                      }`}>
                        {getStrengthText()}
                      </span>
                    </div>
                    <Progress value={passwordStrength} className={`h-1.5 ${getStrengthColor()}`} />
                  </div>
                )}
                
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input 
                            placeholder="Confirm new password" 
                            type={showConfirmPassword ? "text" : "password"} 
                            {...field} 
                            autoComplete="new-password"
                            className="pr-10"
                          />
                        </FormControl>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="sr-only">
                            {showConfirmPassword ? "Hide password" : "Show password"}
                          </span>
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-2 text-muted-foreground">
                  <p className="font-medium text-foreground">Password requirements:</p>
                  <ul className="space-y-1 pl-5">
                    <li className="flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${form.watch("password")?.length >= 8 ? "bg-green-500" : "bg-gray-300"}`}></span>
                      At least 8 characters
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${/[A-Z]/.test(form.watch("password") || "") ? "bg-green-500" : "bg-gray-300"}`}></span>
                      One uppercase letter
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${/[a-z]/.test(form.watch("password") || "") ? "bg-green-500" : "bg-gray-300"}`}></span>
                      One lowercase letter
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${/[0-9]/.test(form.watch("password") || "") ? "bg-green-500" : "bg-gray-300"}`}></span>
                      One number
                    </li>
                  </ul>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full mt-6" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting Password...
                    </>
                  ) : "Reset Password"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
